import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { formatUrl } from 'url-lib';

import gettext from 'js-utils/gettext';
import { formatMinorMoney } from 'js-utils/intl';
import { getAPIDateTimeFormats } from 'js-utils/datetime';
import { getVenueDisplay } from './utils';

import CardList from 'eventbrite_design_system/cardList/CardList';
import CopyButtonWithTooltip from 'eventbrite_design_system/copyButton/CopyButtonWithTooltip';
import DateThumbnail from 'eventbrite_design_system/dateThumbnail/DateThumbnail';
import EditListItem from 'eventbrite_design_system/editListItem/EditListItem';
import Icon from 'eventbrite_design_system/icon/Icon';
import {
    isSmallDown,
    isSmallWideDown,
} from 'eventbrite_design_system/utils/mediaQueries';
import Pagination from 'eventbrite_design_system/pagination/Pagination';
import ProgressIndicator from 'eventbrite_design_system/progressIndicator/ProgressIndicator';

import TicketMultiSvg from 'eventbrite_design_system/iconography/icons/TicketMulti';
import { CARD_STYLE_NEUTRAL } from 'eventbrite_design_system/card/constants';
import { TYPE_LINK } from 'eventbrite_design_system/button/constants';
import {
    PAGE_SIZE_MIN,
    PAGE_SIZE_MD,
    PAGE_SIZE_LG,
} from 'eventbrite_design_system/pagination/constants';

import {
    EVENT_SHAPE,
    STATUS_FILTER_PROP_TYPE,
    EVENT_IMAGE_PLACEHOLDER_URL,
    DEFAULT_PAGE_NUMBER,
    VIEW_ACTION_TYPE,
    EDIT_ACTION_TYPE,
    COPY_URL_ACTION_TYPE,
    COPY_EVENT_ACTION_TYPE,
    POSTPONE_ACTION_TYPE,
    GA_MANAGE_EVENTS_BUTTON_DIMENSION,
} from '../constants';

import SalesStatusColumn from './SalesStatusColumn';
import SalesStatusLabel from './SalesStatusLabel';

import './eventsList.scss';

// Event source constants copied from:
// https://github.com/eventbrite/eb_constants/blob/master/python/eb_constants/event_service_constants/static_constants.py#L145-L146
const EVENT_SOURCE_ORIGINAL = '';
const EVENT_SOURCE_CREATE2 = 'create_2.0';

const SALES_GRID_CLASSES = {
    sharedClasses:
        'eds-g-cell eds-g-cell-lg-4-12 eds-show-up-lg eds-l-pad-hor-2',
    sharedClassesNoPadding:
        'eds-g-cell eds-g-cell-lg-4-12 eds-show-up-lg eds-l-pad-hor-0',
    smallFirstColClasses:
        'eds-g-cell eds-g-cell-12-12 eds-g-cell-lg-4-12 eds-l-pad-hor-2',
    largeFirstColClasses:
        'eds-g-cell eds-g-cell-12-12 eds-g-cell-lg-8-12 eds-l-pad-hor-2',
};

const _renderCopyButton = (copyValue) => {
    // had to name this render prop and return it because of eslint!
    const render = ({ children, containerClassName }) => {
        const renderChildren = () => children;

        return (
            <CopyButtonWithTooltip
                render={renderChildren}
                value={copyValue}
                tooltipId={`tooltip-id-${copyValue.slice(0, 10)}`}
                __tooltipContainerClassName={containerClassName}
            />
        );
    };

    return render;
};

const TimeAndVenue = ({ event, hasUpcomingEvent }) => {
    const { fullDate, fullDateWeekday, time } = getAPIDateTimeFormats(
        event.start,
    );
    // Translators: This is a date and time together. For example: "August 27, 2019 at 8:00 PM PST"
    const smallScreenDateTime = gettext('%(date)s at %(time)s', {
        date: fullDate,
        time,
    });
    // Translators: This is a date and time together. For example: "Wednesday, August 27, 2019 at 8:00 PM PST"
    const largeScreenDateTime = gettext('%(date)s at %(time)s', {
        date: fullDateWeekday,
        time,
    });

    let upcoming;

    if (hasUpcomingEvent) {
        upcoming = (
            <span className="eds-text-weight--heavy eds-l-pad-right-1">
                {gettext('Upcoming:')}&nbsp;
            </span>
        );
    }

    return (
        <div className="eds-l-mar-vert-1">
            <p
                className="eds-show-down-ln"
                data-spec="event-list-item-datetime"
            >
                {upcoming}
                {smallScreenDateTime}
            </p>
            <p data-spec="event-list-item-venue">{getVenueDisplay(event)}</p>
            <p className="eds-show-up-lg" data-spec="event-list-item-time">
                {upcoming}
                {largeScreenDateTime}
            </p>
        </div>
    );
};

const DateSquare = ({ event }) => (
    <div className="eds-show-up-lg">
        <DateThumbnail date={event.start.local} />
    </div>
);

const FinancialInfo = ({
    salesData,
    currency,
    currencyFormat,
    isSmall = false,
}) => {
    const { gross } = salesData;
    let content;
    let className;

    if (!isNil(gross)) {
        const grossMoney = formatMinorMoney(gross, currency, currencyFormat);
        const grossMessage = gettext('%(grossMoney)s Gross', { grossMoney });

        content = isSmall ? grossMessage : grossMoney;
    }

    if (isSmall) {
        className = 'eds-text-color--grey-900 eds-l-mar-bot-1';
    }

    return (
        <p className={className} data-spec="event-list-item-gross">
            {content}
        </p>
    );
};

const SeriesDisplay = () => (
    <p data-spec="event-list-item-series">
        <Icon
            color="ui-900"
            type={
                <TicketMultiSvg
                    size="xsmall"
                    title={gettext('Multiple Events')}
                />
            }
        />
        <span className="l-mar-left-1 eds-text-color--ui-900">
            {gettext('Multiple Events')}
        </span>
    </p>
);

const TertiaryContent = ({ event, currencyFormat, isSeries }) => {
    const { salesDataWithNull, currency } = event;
    let financialInfo;

    if (salesDataWithNull) {
        financialInfo = (
            <FinancialInfo
                salesData={salesDataWithNull}
                currency={currency}
                currencyFormat={currencyFormat}
                isSmall={true}
            />
        );
    }

    let seriesDisplay;

    if (isSeries) {
        seriesDisplay = <SeriesDisplay />;
    }

    return (
        <div>
            <div className="eds-show-down-ln">{financialInfo}</div>
            {seriesDisplay}
            <SalesStatusLabel id={event.id} statusInfo={event} />
        </div>
    );
};

const SalesInfo = ({ salesData }) => {
    const { totalTicketSales: sold, maxPossibleTicketSales: total } = salesData;
    // Translators: This shows how many tickets have been sold out of the total amount. For example: "3601 / 4200"
    const soldMessage = gettext('%(sold)s / %(total)s', { sold, total });
    const progress = (sold / total) * 100;

    return (
        <div>
            <p
                className="eds-align--sm-right eds-align--sn-right"
                data-spec="event-list-item-sold"
            >
                {soldMessage}
            </p>
            <div className="eds-show-up-mn">
                <ProgressIndicator
                    size="large-thin"
                    shape="linear"
                    type="determinate"
                    style="gradient"
                    progress={progress || 0}
                />
            </div>
        </div>
    );
};

const ExternallyTicketed = () => {
    const externalTicketMessage = gettext('Externally Ticketed');

    return (
        <div>
            <p data-spec="event-list-item-external">{externalTicketMessage}</p>
        </div>
    );
};

const ContentsGrid = ({ firstCol, secondCol, thirdCol, isHeading = false }) => {
    let firstColClasses = SALES_GRID_CLASSES.largeFirstColClasses;
    let secondColContent;
    let thirdColClassName = SALES_GRID_CLASSES.sharedClassesNoPadding;

    if (secondCol) {
        firstColClasses = SALES_GRID_CLASSES.smallFirstColClasses;
        secondColContent = (
            <div
                className={SALES_GRID_CLASSES.sharedClasses}
                data-spec="contents-grid-second-column"
            >
                {secondCol}
            </div>
        );
    }

    if (isHeading) {
        thirdColClassName = SALES_GRID_CLASSES.sharedClasses;
    }

    return (
        <div className="eds-g-group eds-text-color--grey-600">
            <div className={firstColClasses}>{firstCol}</div>
            {secondColContent}
            <div className={thirdColClassName}>{thirdCol}</div>
        </div>
    );
};

const NotApplicable = () => <p className="eds-show-up-sw">&mdash;</p>;

const _getActionItems = (eventId, serverUrl, source) => {
    const eventUrl = `${serverUrl}/e/${eventId}`;
    const viewUrl = `/e/${eventId}`;
    const editUrl = formatUrl('/edit', { eid: eventId });
    // This is the copy event url that will direct the user to the
    // appropriate copy experience based on classic or new create
    // and if the copy old to new feature is available
    const copyEventUrl = `/myevent/${eventId}/copy`;
    const postponeUrl = formatUrl(`/manage/events/${eventId}/tickets/`, {
        showPostponeModal: true,
        source: GA_MANAGE_EVENTS_BUTTON_DIMENSION,
    });
    let postponeItem = {
        content: gettext('Postpone'),
        value: POSTPONE_ACTION_TYPE,
        buttonType: TYPE_LINK,
        path: postponeUrl,
    };

    if (source === EVENT_SOURCE_ORIGINAL || source === EVENT_SOURCE_CREATE2) {
        // We do not plan to support postponing old create events via an action item
        postponeItem = null;
    }

    let items = [
        {
            content: gettext('View'),
            value: VIEW_ACTION_TYPE,
            buttonType: TYPE_LINK,
            path: viewUrl,
        },
        {
            content: gettext('Edit'),
            value: EDIT_ACTION_TYPE,
            buttonType: TYPE_LINK,
            path: editUrl,
        },
        {
            content: gettext('Copy URL'),
            renderContainer: _renderCopyButton(eventUrl),
            value: COPY_URL_ACTION_TYPE,
        },
        {
            content: gettext('Copy Event'),
            value: COPY_EVENT_ACTION_TYPE,
            buttonType: TYPE_LINK,
            path: copyEventUrl,
        },
    ];

    if (postponeItem) {
        items = [...items, postponeItem];
    }

    return items;
};

export default class EventsList extends PureComponent {
    static propTypes = {
        serverUrl: PropTypes.string.isRequired,
        events: PropTypes.arrayOf(EVENT_SHAPE).isRequired,
        currencyFormat: PropTypes.string.isRequired,
        statusFilter: STATUS_FILTER_PROP_TYPE.isRequired,
        onPageChange: PropTypes.func.isRequired,
        pageNumber: PropTypes.number,
        pageCount: PropTypes.number,
    };

    static defaultProps = {
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageCount: 1,
    };

    state = {
        isSmallWideDownViewport: false,
        isSmallDownViewport: false,
    };

    componentDidMount = () => {
        this.setState({
            isSmallWideDownViewport: isSmallWideDown(),
            isSmallDownViewport: isSmallDown(),
        });
    };

    _renderItems = () => {
        const { serverUrl, events, currencyFormat } = this.props;
        const items = events.map((event) => {
            const {
                id,
                name: { text: eventName },
                salesDataWithNull,
                currency,
                logo,
                isExternallyTicketed,
                isSeries,
                upcomingEvent,
                source,
            } = event;
            const eventUrl = formatUrl('/myevent', { eid: id });
            const logoUrl = logo ? logo.url : EVENT_IMAGE_PLACEHOLDER_URL;
            const eventData = upcomingEvent || event;
            let firstColComponent = <NotApplicable />;
            let secondColComponent = <NotApplicable />;

            if (salesDataWithNull) {
                firstColComponent = <SalesInfo salesData={salesDataWithNull} />;
                secondColComponent = (
                    <FinancialInfo
                        salesData={salesDataWithNull}
                        currency={currency}
                        currencyFormat={currencyFormat}
                    />
                );
            }

            if (isExternallyTicketed) {
                firstColComponent = <ExternallyTicketed />;
                secondColComponent = undefined;
            }

            return {
                id,
                content: (
                    <EditListItem
                        key={id}
                        href={eventUrl}
                        content={eventName}
                        secondaryContent={
                            <TimeAndVenue
                                event={eventData}
                                hasUpcomingEvent={!!upcomingEvent}
                            />
                        }
                        tertiaryContent={
                            <TertiaryContent
                                event={event}
                                currencyFormat={currencyFormat}
                                isSeries={isSeries}
                            />
                        }
                        imageUrl={logoUrl}
                        extraContent={<DateSquare event={eventData} />}
                        isSquareImage={true}
                        actionItems={_getActionItems(id, serverUrl, source)}
                        noWrap={true}
                    >
                        <div className="event-list-item__content eds-l-pad-right-10">
                            <ContentsGrid
                                firstCol={firstColComponent}
                                secondCol={secondColComponent}
                                thirdCol={
                                    <SalesStatusColumn statusInfo={event} />
                                }
                            />
                        </div>
                    </EditListItem>
                ),
            };
        });

        return <CardList items={items} style={CARD_STYLE_NEUTRAL} />;
    };

    render() {
        const { pageNumber, pageCount, onPageChange } = this.props;
        const { isSmallWideDownViewport, isSmallDownViewport } = this.state;
        let paginationSize = PAGE_SIZE_LG;

        if (isSmallDownViewport) {
            paginationSize = PAGE_SIZE_MIN;
        } else if (isSmallWideDownViewport) {
            paginationSize = PAGE_SIZE_MD;
        }

        return (
            <div
                className="events-list events-list-sales-status"
                data-spec="events-list"
            >
                <EditListItem
                    __containerClassName="eds-show-up-sw eds-g-group eds-bg-color--grey-100 eds-l-pad-hor-6 eds-l-pad-vert-4"
                    content={
                        <span className="eds-text-bm eds-text-weight--heavy">
                            {gettext('Event')}
                        </span>
                    }
                    actionItems={[]}
                    noWrap={true}
                >
                    <div className="event-list-item__content eds-align--center-vertical">
                        <ContentsGrid
                            firstCol={
                                <span className="eds-text-weight--heavy">
                                    {gettext('Sold')}
                                </span>
                            }
                            secondCol={
                                <span className="eds-text-weight--heavy">
                                    {gettext('Gross')}
                                </span>
                            }
                            thirdCol={
                                <span className="eds-text-weight--heavy">
                                    {gettext('Status')}
                                </span>
                            }
                            isHeading={true}
                        />
                    </div>
                </EditListItem>
                <div data-spec="events-list-items">{this._renderItems()}</div>
                <Pagination
                    pageNumber={pageNumber}
                    pageCount={pageCount}
                    size={paginationSize}
                    onSelected={onPageChange}
                    showOnSinglePage={false}
                />
            </div>
        );
    }
}
